import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { type CarouselRef } from 'antd5/es/carousel';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyState from 'components/EmptyState';
import { Button } from 'components/ui';
import {
  type ComparePicturesParams,
  type PreviousDamageInfo,
  type PreviousInspectionInfo,
} from 'horizon/components/Damages/HorizonDamageImage/types';
import { getThumbnailCompareData } from 'horizon/components/Damages/HorizonDamageImage/utils';

import { type ImageThumbnailProps, ImageThumbnail } from './ImageThumbnail';
import { CarouselContainer, EmptyImage, StyledCarousel } from './ImageThumbnail.style';

type ThumbnailCarouselProps = {
  thumbnails: {
    [K in keyof Omit<ImageThumbnailProps, 'handleClickObservation' | 'allowZoom'>]?:
      | ImageThumbnailProps[K]
      | null
      | undefined;
  }[]; // Partial<T>, but allow null | undefined (to allow for null or undefined picture, which is handled when rendering)
  initialSlideIndex?: number;
  allowZoom?: boolean;
  shouldShowSeverity?: boolean;
  shouldShowDistance?: boolean;
  shouldShowExpand?: boolean;
  previousDamageInfo?: PreviousDamageInfo;
  prevInspectionDetails?: PreviousInspectionInfo;
  handleCompareNavigation?: ({
    previousDamageInfo,
    previousInspectionInfo,
  }: ComparePicturesParams) => Promise<void>;
  compareInspectionPicturesLoading?: boolean;
};

export const ThumbnailCarousel: React.FunctionComponent<ThumbnailCarouselProps> = ({
  thumbnails,
  initialSlideIndex,
  allowZoom = false,
  shouldShowSeverity,
  shouldShowDistance,
  shouldShowExpand,
  previousDamageInfo,
  prevInspectionDetails,
  handleCompareNavigation,
  compareInspectionPicturesLoading,
}) => {
  const { t } = useTranslation();
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlideIndex ?? 0);

  return (
    <CarouselContainer>
      {thumbnails.length > 1 && (
        <>
          <Button
            className="prev-button"
            title={t('images.previousImage', 'Previous Image')}
            disabled={currentSlide === 0}
            icon={<LeftOutlined />}
            onClick={() => {
              carouselRef.current?.prev();
            }}
          />
          <Button
            className="next-button"
            title={t('images.nextImage', 'Next Image')}
            disabled={currentSlide === thumbnails.length - 1}
            icon={<RightOutlined />}
            onClick={() => {
              carouselRef.current?.next();
            }}
          />
        </>
      )}
      <StyledCarousel
        ref={carouselRef}
        initialSlide={initialSlideIndex ?? 0}
        afterChange={setCurrentSlide}
        dots={thumbnails.length > 1}
      >
        {thumbnails.map((thumbnailProps, i) => {
          if (!thumbnailProps.picture) {
            return (
              <EmptyImage key={i}>
                <EmptyState text={t('images.noImageAvailable', 'No Image Available')} />
              </EmptyImage>
            );
          }

          // Compare opens this slide's image on the left, keeping the resolved previous
          // (right) image unchanged.
          const {
            previousDamageInfo: thumbnailPreviousDamageInfo,
            prevInspectionDetails: thumbnailPrevInspectionDetails,
          } = getThumbnailCompareData(
            { previousDamageInfo, prevInspectionDetails },
            thumbnailProps.observations?.[0]
          );

          return (
            <ImageThumbnail
              key={i}
              {...(thumbnailProps as Omit<ImageThumbnailProps, 'handleClickObservation'>)}
              allowZoom={allowZoom}
              shouldShowSeverity={shouldShowSeverity}
              shouldShowDistance={shouldShowDistance}
              shouldShowExpand={shouldShowExpand}
              $isInCarousel={thumbnails.length > 1}
              previousDamageInfo={thumbnailPreviousDamageInfo}
              prevInspectionDetails={thumbnailPrevInspectionDetails}
              handleCompareNavigation={handleCompareNavigation}
              compareInspectionPicturesLoading={compareInspectionPicturesLoading}
            />
          );
        })}
      </StyledCarousel>
    </CarouselContainer>
  );
};
